import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { INTERACTIVE_TOOLTIP_HIDE_DELAY_MS } from 'constants/tooltip';
import { FormattedMessage } from 'react-intl';

import { Spinner } from 'admin-components/components';
import { GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS } from 'store/queries';
import { getBookingJourneysURL } from 'utils/urls';

import {
  BookingLink,
  SectionTableContainer,
  SectionTitleContainer,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow
} from '../ClubEurostarMember.styles';
import { RemainingLabel, TooltipContainer } from './MemberInfo.styles';

const TRIPS_REQUIRED = 30;
const BENEFIT_STATUS_AVAILABLE = 'Available';
const BENEFIT_STATUS_USED = 'Used';

const BENEFIT_CONFIG = [
  {
    key: 'business-standard',
    headerId: 'clubEurostar.businessRewardsBenefits.header.plusUpgrade',
    tripProgressId: 'clubEurostar.businessRewardsBenefits.tripProgress.standard',
    tripsKey: 'standardTrips',
    benefitTypeName: 'Ticket Upgrade',
    benefitName: 'Ticket Upgrade - Business Standard'
  },
  {
    key: 'business-plus',
    headerId: 'clubEurostar.businessRewardsBenefits.header.premierUpgrade',
    tripProgressId: 'clubEurostar.businessRewardsBenefits.tripProgress.plus',
    tripsKey: 'plusTrips',
    benefitTypeName: 'Ticket Upgrade',
    benefitName: 'Ticket Upgrade - Business Plus'
  },
  {
    key: 'business-premier',
    headerId: 'clubEurostar.businessRewardsBenefits.header.premierReturn',
    tripProgressId: 'clubEurostar.businessRewardsBenefits.tripProgress.premier',
    tripsKey: 'premierTrips',
    benefitTypeName: 'Free Premier Ticket',
    benefitName: 'Ticket Upgrade - Business Premier'
  }
];

const getLatestEndDate = (benefits) =>
  benefits.reduce((latestDate, benefit) => {
    if (!benefit?.endDate) return latestDate;
    const benefitDate = moment(benefit.endDate);
    if (!latestDate || benefitDate.isAfter(latestDate)) {
      return benefitDate;
    }
    return latestDate;
  }, null);

const BusinessRewardsBenefits = ({ membershipNumber }) => {
  const shouldSkip = !membershipNumber;
  const { data, loading, error } = useQuery(GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS, {
    variables: {
      membershipNumber
    },
    fetchPolicy: 'network-only',
    skip: shouldSkip
  });

  if (shouldSkip) {
    return null;
  }

  if (!data?.getLoyaltyBusinessAccountBenefits) {
    return null;
  }

  const { tripSummary = {}, benefitList } = data?.getLoyaltyBusinessAccountBenefits ?? {};
  const benefits = benefitList ?? [];
  const premierTrips = tripSummary?.premierTrips ?? tripSummary?.premiumTrips ?? 0;
  const tripCounts = {
    standardTrips: tripSummary?.standardTrips ?? 0,
    plusTrips: tripSummary?.plusTrips ?? 0,
    premierTrips
  };
  const membershipNumberForUrl = membershipNumber ?? null;

  const renderCells = () =>
    BENEFIT_CONFIG.map(({ key, tripProgressId, tripsKey, benefitTypeName, benefitName }) => {
      const matchingBenefits = benefits.filter(
        (benefit) => benefit?.benefitTypeName === benefitTypeName && benefit?.benefitName === benefitName
      );
      const availableCount = matchingBenefits.filter((benefit) => benefit?.benefitStatus === BENEFIT_STATUS_AVAILABLE).length;
      const usedBenefits = matchingBenefits.filter((benefit) => benefit?.benefitStatus === BENEFIT_STATUS_USED);
      const usedCount = usedBenefits.length;
      const totalCount = availableCount + usedCount;
      const tooltipId = `business-benefits-${key}-tooltip`;
      const endDate = getLatestEndDate(matchingBenefits);
      const endDateLabel = endDate ? endDate.format('Do MMMM YYYY') : null;
      const availableLabel = (
        <RemainingLabel data-for={tooltipId} data-tip="">
          {availableCount}
        </RemainingLabel>
      );

      return (
        <StyledTableCell span={1} key={key}>
          <div>
            <FormattedMessage
              id={tripProgressId}
              values={{
                completed: tripCounts[tripsKey],
                required: TRIPS_REQUIRED
              }}
            />
          </div>
          <FormattedMessage
            id="clubEurostar.businessRewardsBenefits.available"
            tagName="div"
            values={{
              available: availableLabel,
              total: totalCount
            }}
          />
          <ReactTooltip
            className="tooltip"
            id={tooltipId}
            place="bottom"
            effect="solid"
            delayHide={INTERACTIVE_TOOLTIP_HIDE_DELAY_MS}
            clickable={true}
          >
            <TooltipContainer>
              <FormattedMessage id="clubEurostar.businessRewardsBenefits.tooltip.title" tagName="strong" />
              {usedBenefits.length === 0 ? (
                <div>
                  <FormattedMessage
                    id={
                      endDateLabel
                        ? 'clubEurostar.businessRewardsBenefits.tooltip.emptyWithDate'
                        : 'clubEurostar.businessRewardsBenefits.tooltip.empty'
                    }
                    values={endDateLabel ? { endDate: endDateLabel } : undefined}
                  />
                </div>
              ) : (
                <ul>
                  {usedBenefits.map(({ id, pnr }) =>
                    pnr ? (
                      <li key={id ?? pnr}>
                        <BookingLink href={getBookingJourneysURL(pnr, membershipNumberForUrl)} target="_blank" rel="noreferrer">
                          {pnr}
                        </BookingLink>
                      </li>
                    ) : null
                  )}
                </ul>
              )}
            </TooltipContainer>
          </ReactTooltip>
        </StyledTableCell>
      );
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }} data-testid="business-rewards-benefits">
      <SectionTitleContainer>
        <FormattedMessage id="clubEurostar.businessRewardsBenefits.title" tagName="h3" />
      </SectionTitleContainer>
      <SectionTableContainer>
        <StyledTableRow columns={3}>
          {BENEFIT_CONFIG.map(({ key, headerId }) => (
            <StyledTableHeader span={1} justifyContent="center" alignItems="center" key={key}>
              <FormattedMessage id={headerId} />
            </StyledTableHeader>
          ))}
        </StyledTableRow>
        {loading ? (
          <StyledTableRow columns={3}>
            <StyledTableCell span={3} justifyContent="center" alignItems="center">
              <Spinner type={'circular'} />
            </StyledTableCell>
          </StyledTableRow>
        ) : error ? (
          <StyledTableRow columns={3}>
            <StyledTableCell span={3} justifyContent="center" alignItems="center">
              -
            </StyledTableCell>
          </StyledTableRow>
        ) : (
          <StyledTableRow columns={3}>{renderCells()}</StyledTableRow>
        )}
      </SectionTableContainer>
    </div>
  );
};

BusinessRewardsBenefits.propTypes = {
  membershipNumber: PropTypes.string
};

export default BusinessRewardsBenefits;
